// Vendor imports
const _ = require('lodash')
// Client lib imports
const uploader = require('client/lib/uploader')
const { FriendlyRequestError } = require('client/lib/request')
const { send: ajax } = require('client/lib/request')
const businessEvents = require('client/lib/business-events')
// Client V2 common imports
const confirmModalActions = require('client-v2/common/containers/confirm-modal/actions')
const toastsActions = require('client-v2/common/containers/toasts/actions')
// Cousin imports
const collectionActions = require('../collection/actions')
const collectionEditModalSelectors = require('../collection-edit-modal/selectors')
// Sibling imports
const selectors = require('./selectors')

// Action types

const SHOW = 'COLLECTION_EDIT_MODAL_SHOW'
const HIDE = 'COLLECTION_EDIT_MODAL_HIDE'
const COLLECTION_UPDATE_PENDING = 'COLLECTION_EDIT_MODAL_UPDATE_PENDING'
const COLLECTION_UPDATE_SUCCESS = 'COLLECTION_EDIT_MODAL_UPDATE_SUCCESS'
const COLLECTION_UPDATE_FAILED = 'COLLECTION_EDIT_MODAL_UPDATE_FAILED'
const COLLECTION_DELETE_PENDING = 'COLLECTION_EDIT_MODAL_DELETE_PENDING'
const COLLECTION_DELETE_SUCCESS = 'COLLECTION_EDIT_MODAL_DELETE_SUCCESS'
const COLLECTION_DELETE_FAILED = 'COLLECTION_EDIT_MODAL_DELETE_FAILED'
const HEADER_IMAGE_UPLOAD_PENDING = 'COLLECTION_EDIT_MODAL_HEADER_IMAGE_UPLOAD_PENDING'
const HEADER_IMAGE_UPLOAD_SUCCESS = 'COLLECTION_EDIT_MODAL_HEADER_IMAGE_UPLOAD_SUCCESS'
const HEADER_IMAGE_UPLOAD_FAILED = 'COLLECTION_EDIT_MODAL_HEADER_IMAGE_UPLOAD_FAILED'

// Action creators

function hide() {
  return {
    type: HIDE
  }
}

// Action dispatchers

function show() {
  return async function (dispatch, getState) {
    const { collection } = getState()
    dispatch({
      type: SHOW,
      payload: collection
    })
  }
}

function deleteCollection() {
  return async function (dispatch, getState) {
    const state = getState()
    if (selectors.isPending(state)) {
      return
    }
    if (await dispatch(confirmModalActions.confirm({
      ariaLabel: 'Delete collection',
      heading: 'Are you sure you want to delete this collection?',
      subheading: 'Posts from this collection will still be mixed to your profile.'
    }))) {
      dispatch({
        type: COLLECTION_DELETE_PENDING
      })
      const collection = collectionEditModalSelectors.collection(state)
      try {
        const result = await ajax({
          url: `/api/v0.2/mix/${collection.id}`,
          method: 'delete'
        })
        dispatch({
          type: COLLECTION_DELETE_SUCCESS,
          payload: result
        })
        await businessEvents.trackSegment('delete-mix', {
          'mix_id': collection.id
        })
        dispatch(collectionActions.collectionDeleted(collection.id))
      } catch (err) {
        dispatch({
          type: COLLECTION_DELETE_FAILED
        })
        dispatch(confirmModalActions.alert({
          heading: 'Failed to delete collection',
          subheading: err.message
        }))
        throw err
      }
    }
  }
}

function submitCollection(data) {
  return async function (dispatch) {
    await dispatch(updateCollection(data))
    dispatch(hide())
  }
}

function updateCollection(data) {
  return async function (dispatch, getState) {
    const state = getState()
    if (selectors.isPending(state)) {
      return
    }
    dispatch({
      type: COLLECTION_UPDATE_PENDING
    })
    const collection = collectionEditModalSelectors.collection(state)
    try {
      const result = await ajax({
        url: `/api/v0.1/mix/${collection.id}`,
        method: 'put',
        contentType: 'application/json',
        data
      })
      const collectionUpdated = collection.merge(_.pick(result, [
        'name',
        'description',
        'visibility',
        'cover_image_url'
      ]))
      await businessEvents.trackSegment('update-mix', {
        'mix_id': collection.id
      })
      dispatch({
        type: COLLECTION_UPDATE_SUCCESS,
        payload: collectionUpdated
      })
      dispatch(collectionActions.collectionEdited(collectionUpdated))
    } catch (err) {
      dispatch({
        type: COLLECTION_UPDATE_FAILED
      })
      dispatch(confirmModalActions.alert({
        heading: 'Failed to update collection',
        subheading: err.message
      }))
      throw err
    }
  }
}

function uploadHeaderImage({ file }) {
  return async function (dispatch, getState) {
    const state = getState()
    if (selectors.isPending(state)) {
      return
    }
    dispatch({
      type: HEADER_IMAGE_UPLOAD_PENDING
    })
    const { csrfToken } = state.env
    const collection = collectionEditModalSelectors.collection(state)
    const name = 'mix_header_image'
    const url = `/api/v0.1/mix/${collection.id}/upload-header-image`
    try {
      const value = await uploader(url, name, file, { csrfToken })
      const collectionUpdated = collection.mergeDeep({
        'header_image_urls': { large: value }
      })
      dispatch({
        type: HEADER_IMAGE_UPLOAD_SUCCESS,
        payload: collectionUpdated
      })
      dispatch(collectionActions.collectionEdited(collectionUpdated))
      dispatch(toastsActions.showNotice({
        message: `Collection banner image has been updated`
      }))
    } catch (err) {
      dispatch({
        type: HEADER_IMAGE_UPLOAD_FAILED,
        payload: { error: err }
      })
      let message = 'Sorry. We were not able to upload that image.'
      if (err instanceof FriendlyRequestError) {
        message = err.message
      }
      dispatch(confirmModalActions.alert({
        heading: 'Failed to upload collection banner image',
        subheading: message
      }))
      throw err
    }
  }
}

function deleteHeaderImage() {
  return async function (dispatch, getState) {
    const state = getState()
    if (selectors.isPending(state)) {
      return
    }
    const collection = collectionEditModalSelectors.collection(state)
    dispatch({
      type: HEADER_IMAGE_UPLOAD_PENDING
    })
    try {
      await ajax({
        url: `/api/v0.1/mix/${collection.id}/upload-header-image`,
        method: 'delete'
      })
      const collectionUpdated = collection.merge({
        'header_image_urls': null
      })
      dispatch({
        type: HEADER_IMAGE_UPLOAD_SUCCESS,
        payload: collectionUpdated
      })
      dispatch(collectionActions.collectionEdited(collectionUpdated))
      dispatch(toastsActions.showNotice({
        message: `Collection banner image has been deleted`
      }))
    } catch (err) {
      dispatch({
        type: HEADER_IMAGE_UPLOAD_FAILED,
        payload: { error: err }
      })
      let message = 'Sorry. We were not able to delete that image.'
      if (err instanceof FriendlyRequestError) {
        message = err.message
      }
      dispatch(confirmModalActions.alert({
        heading: 'Failed to delete collection banner image',
        subheading: message
      }))
      throw err
    }
  }
}

// Exports

module.exports = {
  // Action types
  SHOW,
  HIDE,
  COLLECTION_UPDATE_PENDING,
  COLLECTION_UPDATE_SUCCESS,
  COLLECTION_UPDATE_FAILED,
  COLLECTION_DELETE_PENDING,
  COLLECTION_DELETE_SUCCESS,
  COLLECTION_DELETE_FAILED,
  HEADER_IMAGE_UPLOAD_PENDING,
  HEADER_IMAGE_UPLOAD_FAILED,
  HEADER_IMAGE_UPLOAD_SUCCESS,
  // Action creators
  hide,
  // Action dispatchers
  show,
  deleteCollection,
  submitCollection,
  updateCollection,
  deleteHeaderImage,
  uploadHeaderImage
}
